import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { BuzzlyLogo } from "@/components/BuzzlyLogo";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";

function Field({ label, id, ...props }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold text-[#050505]">{label}</span>
      <input id={id} {...props}
        className="mt-1.5 w-full rounded-lg border border-[#ccd0d5] bg-white px-4 py-3 text-[15px] outline-none transition-shadow focus:border-[#1877f2] focus:shadow-[0_0_0_3px_rgba(24,119,242,0.15)]" />
    </label>
  );
}

export default function AuthPage({ mode }) {
  const isRegister = mode === "register";
  const isAdmin = mode === "admin";
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", password: "", referral_code: params.get("ref") || "" });
  const [busy, setBusy] = useState(false);
  const { signIn } = useAuth();
  const nav = useNavigate();
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = isRegister
        ? await api.register({ ...form, referral_code: form.referral_code || null })
        : await api.login({ email: form.email, password: form.password });
      if (isAdmin && data.user?.role !== "admin") {
        toast.error("Ce compte n'a pas les droits administrateur");
        return;
      }
      signIn(data);
      toast.success(isRegister ? "Bienvenue sur BuzzlyMe !" : isAdmin ? "Bienvenue dans l'espace admin" : "Connexion réussie");
      nav(isRegister ? "/app/agent" : isAdmin || data.user?.role === "admin" ? "/app/admin" : "/app");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f0f2f5] flex flex-col items-center justify-center px-4 py-12" data-testid={`${mode}-page`}>
      <BuzzlyLogo />
      <form onSubmit={submit} className="mt-8 w-full max-w-md bg-white rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.08)] p-8 space-y-4 fade-up" data-testid={`${mode}-form`}>
        <div>
          <h1 className="font-display text-2xl font-extrabold">{isRegister ? "Créez votre agent de veille" : isAdmin ? "Espace administrateur" : "Connexion à BuzzlyMe"}</h1>
          <p className="text-sm text-[#65676b] mt-1">{isRegister ? "7 jours gratuits, puis 19,99 €/mois." : isAdmin ? "Accès réservé à l'administrateur de BuzzlyMe." : "Retrouvez vos buzz et vos vidéos."}</p>
        </div>
        {isRegister && <Field label="Nom ou nom de page" id="name" value={form.name} onChange={set("name")} required data-testid="register-name-input" />}
        <Field label="Email" id="email" type="email" value={form.email} onChange={set("email")} required autoComplete="email" data-testid={`${mode}-email-input`} />
        <Field label="Mot de passe" id="password" type="password" minLength={isRegister ? 8 : undefined} value={form.password} onChange={set("password")} required
          autoComplete={isRegister ? "new-password" : "current-password"} data-testid={`${mode}-password-input`} />
        {isRegister && <Field label="Code parrain (optionnel)" id="ref" value={form.referral_code} onChange={set("referral_code")} data-testid="register-referral-input" />}
        <button type="submit" disabled={busy} className="btn-primary w-full justify-center !py-3 !text-base" data-testid={`${mode}-submit-btn`}>
          {busy ? "Un instant…" : isRegister ? "Créer mon compte" : "Se connecter"}
        </button>
        {!isAdmin && <p className="text-sm text-center text-[#65676b]">
          {isRegister ? "Déjà inscrit ? " : "Pas encore de compte ? "}
          <Link to={isRegister ? "/login" : "/register"} className="font-semibold text-[#1877f2] hover:underline" data-testid={`${mode}-switch-link`}>
            {isRegister ? "Se connecter" : "Essai gratuit 7 jours"}
          </Link>
        </p>}
        {!isRegister && (
          <p className="text-xs text-center">
            <Link to={isAdmin ? "/login" : "/admin"} className="font-semibold text-[#65676b] hover:text-[#1877f2]" data-testid={`${mode}-admin-switch-link`}>
              {isAdmin ? "← Connexion abonné" : "Espace administrateur"}
            </Link>
          </p>
        )}
      </form>
    </div>
  );
}
